<template>
  <div class="bonus-table-container relative">
    <div class="bonus-table-header sticky top-0 z-10">
      <div class="date-select">
        <Border radius="6px" borderWidth="1px">
          <div class="date-btn w-full h-[32px] flex items-center justify-between" @click="showOptions = !showOptions">
            {{ t(`historico.${dateOptions[selectedDate].key}`) }}
            <span :class="['arrow', showOptions ? 'up' : 'down']"></span>
          </div>
        </Border>
        <div v-if="showOptions" class="date-options bg-[#005E4B]">
          <button v-for="(item, idx) in dateOptions" :key="item.value"
            :class="['option-btn', { active: idx === selectedDate }]" @click="selectDate(idx)">
            {{ t(`historico.${item.key}`) }}
          </button>
        </div>
      </div>
      <div class="bonus-value font-bold text-[11px] text-white flex">
        <div v-if="!IS_DOUBLE_WALLET">
          {{ t('historico.cash') }} <span class="text-[#F6FF00]">{{ numberFormat(totalAmount) }} {{ VITE_symbol
          }}</span>
          +
          &nbsp;
        </div>
        <div>
          {{ t('historico.bonus') }} <span class="text-[#F6FF00]">{{ numberFormat(totalBonusAmount) }} </span>
        </div>

      </div>
    </div>
    <div class="p-[1px] mt-[12.5px] rounded-[6px]" style="background: linear-gradient(0deg,#005E4B,#00A48B);">
      <div class="bonus-table p-[3px] pt-0 rounded-[6px]" style="background: linear-gradient(0deg, #005142, #007B62)">
        <div class="table-header min-h-[31px]">
          <div class="th">{{ t('historico.time') }}</div>
          <div class="th">{{ t('historico.eventName') }}</div>
          <div class="th">{{ t('historico.eventReward') }}</div>
          <div class="th">{{ t('historico.source') }}</div>
        </div>
        <div class="min-h-[280px] relative rounded-[6px] bg-[#003429] border border-solid border-[#002921]">
          <div class="start-line h-[13px] absolute top-0 left-0 w-full rounded-t-[6px]"
            style="background: linear-gradient(0deg, rgba(0,52,41,0.09) 44%, rgba(255,255,255,0.2) 100%);"></div>
          <div v-if="tableData.length > 0" class="relative h-full">
            <div class="h-[50vh] overflow-y-auto">
              <div v-for="(row, idx) in tableData" :key="idx" style="box-shadow: 0px 1px 1px 0px #002118;"
                :class="['table-row',]">
                <div class="td">{{ formatTime(row.createTime) }}</div>
                <div class="td">{{ getTypeText1(row.event) }} </div>
                <div class="td color-[#D3D53B]">{{ row.rewardBound || row.reward }}({{ row.rewardBound > 0 ?
                  t('historico.bonus') : t('historico.cash') }})</div>
                <div class="td">{{ getTypeText(row.source) }}</div>
              </div>
            </div>
            <div
              class="page-btn border-t-1px border-t-solid border-t-[#002921] sticky bottom-0 flex justify-center py-[10px]">
              <div class="flex">
                <div class="w-[13px] h-[11px]">
                  <img class="w-full h-full cursor-pointer" v-if="pageNo > 1" @click="pageNo--; fetchData()"
                    :src="getAssetsFile('activity/btn_zuo.png')" alt="" srcset="">
                </div>
                <div class="mx-[30px] text-white">
                  {{ pageNo }} / {{ Math.ceil(total / pageSize) || 1 }}
                </div>
                <div class="w-[13px] h-[11px]">
                  <img v-if="pageNo < Math.ceil(total / pageSize)" class="w-full h-full cursor-pointer"
                    @click="pageNo++; fetchData()" :src="getAssetsFile('activity/btn_you.png')" alt="" srcset="">
                </div>
              </div>
            </div>
          </div>
          <div v-else class=" w-full h-full">
            <div class="empty-data">
              <div class="flex justify-center items-center">
                <img :src="getAssetsFile('home/No_record@2x.png')" alt=""
                  class="max-h-[84px] max-w-[106.5px] w-auto h-auto">
              </div>
              <div class="empty-text">{{ t('historico.noRecords') }}</div>
            </div>
          </div>
        </div>

      </div>
    </div>

  </div>

  <!-- 登录弹窗 -->
  <AuthDialog :show="showAuthDialog" :is-login="true" @close="handleAuthDialogClose" />
</template>

<script setup lang="ts">
import { Border } from "@/components/Border"
import { ref, onMounted, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import * as GameApi from '@/api/game'
import dayjs from 'dayjs'
import { numberFormat } from '@/utils/formatter'
import { getAssetsFile } from '@/utils/pub-use'
import { useUserStore } from '@/stores/modules/user'
import { useVipStore } from '@/stores/modules/vip'
import AuthDialog from '@/components/AuthDialog.vue'
import { formatTime } from '@/utils/date'

const IS_DOUBLE_WALLET = import.meta.env?.VITE_WALLET_SWITCH == 'true' ? true : false;
const VITE_symbol = import.meta.env.VITE_SYMBOL
const { t } = useI18n()
const router = useRouter()
const userStore = useUserStore()
const vipStore = useVipStore()

// 登录弹窗状态
const showAuthDialog = ref(false)

// 获取当天0点
function getStartOfDay(date = new Date()) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0)
}

// 格式化为 yyyy-MM-dd 格式
function formatDate(date: Date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

const now = new Date()
const today = getStartOfDay(now)
const yesterday = getStartOfDay(new Date(now.getTime() - 24 * 60 * 60 * 1000))
const last7 = getStartOfDay(new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000)) // 近7天含今天
const last30 = getStartOfDay(new Date(now.getTime() - 29 * 24 * 60 * 60 * 1000)) // 近30天含今天

// 修改 dateOptions，添加 key 字段用于多语言
const dateOptions = [
  { key: 'today', label: t('historico.today'), value: formatDate(today) },
  { key: 'yesterday', label: t('historico.yesterday'), value: formatDate(yesterday) },
  { key: 'last7Days', label: t('historico.last7Days'), value: formatDate(last7) },
  { key: 'last30Days', label: t('historico.last30Days'), value: formatDate(last30) }
]

const showOptions = ref(false)
const selectedDate = ref(0)
const tableData = ref([])
const total = ref(0)
const pageNo = ref(1)
const pageSize = ref(10)

const loading = ref(false)
const totalAmount = ref(0)
const totalBonusAmount = ref(0)

// 检查登录状态
function checkLoginStatus() {
  if (!userStore.isLoggedIn) {
    showAuthDialog.value = true
    return false
  }
  return true
}

// 处理登录弹窗关闭
function handleAuthDialogClose() {
  showAuthDialog.value = false
  // 如果用户关闭登录弹窗，跳转回eventos标签页
  router.replace({
    path: '/activity',
    query: { tab: 'eventos' }
  })
}

function selectDate(idx: number) {
  selectedDate.value = idx
  showOptions.value = false
  resetPagination()
  fetchData()
}

// 重置分页
function resetPagination() {
  pageNo.value = 1
  tableData.value = []
  totalAmount.value = 0
  total.value = 0
  fetchData()
}





// 获取时间范围
function getTimeRange() {
  const startTime = dateOptions[selectedDate.value].value
  let endTime = startTime

  // 根据选择的时间范围设置结束时间
  switch (selectedDate.value) {
    case 0: // Today - 今天
      endTime = formatDate(today)
      break
    case 1: // Yesterday - 昨天
      endTime = formatDate(yesterday)
      break
    case 2: // Last 7 Days - 近7天
      endTime = formatDate(today) // 结束时间是今天
      break
    case 3: // Last 30 Days - 近30天
      endTime = formatDate(today) // 结束时间是今天
      break
  }

  return { startTime, endTime }
}

// 请求参数 - 使用新的 rewardData 接口
function fetchData(isAppend = false) {
  if (loading.value) return

  loading.value = true
  const { startTime, endTime } = getTimeRange()

  const params = {
    pageNo: pageNo.value,
    pageSize: pageSize.value,
    startTime: `${startTime} 00:00:00`,
    endTime: `${endTime} 23:59:59`,
  }


  GameApi.rewardData(params).then(res => {
    if (res && res.data) {
      tableData.value = res.data.pageResult.list
      totalAmount.value = res.data.amount
      totalBonusAmount.value = res.data.boundAmount;
      total.value = res.data.pageResult.total
    } else {
      if (!isAppend) {
        tableData.value = []
        totalAmount.value = 0
      }

    }

  }).catch(error => {
    tableData.value = []
    total.value = 0
    totalAmount.value = 0

  }).finally(() => {
    loading.value = false
  })
}
const mockData = (num: number) => {
  return Array.from({ length: num }, (_, index) => ({
    createTime: dayjs().subtract(index, 'day').format('YYYY-MM-DD HH:mm:ss'),
    event: typeMap1[Math.floor(Math.random() * 14) + 1], // typeMap1 有 1-14 共14个值
    reward: Math.floor(Math.random() * 1000) + 1,
    source: typeMap[Math.floor(Math.random() * 9) + 1] // typeMap 有 1-9 共9个值
  }))
}
// 使用多语言映射
const typeMap: Record<number, string> = {
  1: t('historico.returnWater'), // 返水
  2: t('historico.vip'), // VIP
  3: t('historico.vipDayReward'), // vip日奖励
  4: t('historico.vipWeeklyRewards'), // vip周奖励
  5: t('historico.vipMonthlyRewards'), // vip月奖励
  6: t('historico.helpCash'), // 助力领现金
  7: t('historico.dailyRescue'), // 每日救援金
  8: t('historico.weeklyRescue'), // 每周救援金
  9: t('historico.mysteriousMine'), // 神秘矿场
  10: t('historico.mysteriousBounds'), // 神秘彩金
  11: t('historico.continuousSignIn'), // 连续签到
  12: t('historico.firstRechargeRebate'), // 首充返利
  13: t('historico.firstWithdrawRebate'), // 首提返利
  14: t('historico.firstRechargeDoubleRebate'), // 首充加倍返利
  15: t('historico.firstWithdrawDoubleRebate'), // 首提加倍返利
  16: t('historico.dayRecharge'), // 每日充值
  17: t('historico.newUserExclusiveReward'), // 新用户专属奖励
  18: t('historico.rechargeGift'), // 充值赠送
  19: t('historico.promoterTreasure'), // 推广员宝箱
}

function getTypeText(type: number) {
  // 对于VIP相关的类型，需要动态拼接等级
  if (type === 3) { // vip日奖励
    return t('historico.vipDayReward', { level: vipStore.vipInfo.level || 0 })
  } else if (type === 4) { // vip周奖励
    return t('historico.vipWeeklyRewards', { level: vipStore.vipInfo.level || 0 })
  } else if (type === 5) { // vip月奖励
    return t('historico.vipMonthlyRewards', { level: vipStore.vipInfo.level || 0 })
  }

  // 其他类型使用静态映射
  const typeMap: Record<number, string> = {
    1: t('historico.returnWater'), // 返水
    2: t('historico.vip'), // VIP等级奖励
    6: t('historico.helpCash'), // 助力领现金
    7: t('historico.dailyRescue'), // 每日救援金
    8: t('historico.weeklyRescue'), // 每周救援金
    9: t('historico.mysteriousMine'), // 神秘矿场
    10: t('historico.mysteriousBounds'), // 神秘彩金
    11: t('historico.continuousSignIn'), // 连续签到
    12: t('historico.firstRechargeRebate'), // 首充返利
    13: t('historico.firstWithdrawRebate'), // 首提返利
    14: t('historico.firstRechargeDoubleRebate'), // 首充加倍返利
    15: t('historico.firstWithdrawDoubleRebate'), // 首提加倍返利
    16: t('historico.dayRecharge'), // 每日充值
    17: t('historico.newUserExclusiveReward'), // 新用户专属奖励
    18: t('historico.rechargeGift'), // 充值赠送
    19: t('historico.promoterTreasure'), // 推广员宝箱
  }

  return typeMap[type] || type
}

const typeMap1: Record<number, string> = {
  1: t('historico.returnWater'), // 返水 ANTI_WATER
  2: t('historico.vip'), // VIP VIP
  3: t('historico.helpCash'), // 助力领现金 HELP_GET_CASH
  4: t('historico.mysteriousMine'), // 神秘矿场 MYSTERIOUS_MINE
  5: t('historico.dailyRescue'), // 每日救援金 DAY_RESCUE_FUND
  6: t('historico.weeklyRescue'), // 每周救援金 WEEK_RESCUE_FUND
  7: t('historico.mysteriousBounds'), // 神秘彩金 MYSTERIOUS_BOUNDS
  8: t('historico.continuousSignIn'), // 连续签到 CONTINUOUS_SIGN_IN
  9: t('historico.firstRechargeRebate'), // 首充返利 FIRST_RECHARGE_REBATE
  10: t('historico.firstWithdrawRebate'), // 首提返利 FIRST_WITHDRAW_REBATE
  11: t('historico.firstRechargeDoubleRebate'), // 首充加倍返利 FIRST_RECHARGE_DOUBLE_REBATE
  12: t('historico.firstWithdrawDoubleRebate'), // 首提加倍返利 FIRST_WITHDRAW_DOUBLE_REBATE
  13: t('historico.dayRecharge'), // 每日充值 DAY_RECHARGE
  14: t('historico.newUserExclusiveReward'), // 新用户专属奖励 NEW_USER_EXCLUSIVE_REWARD
  15: t('historico.rechargeGift'), // 充值赠送 RECHARGE_GIFT
  16: t('historico.promoterTreasure'), // 推广员宝箱 PROMOTER_TREASURE
}

function getTypeText1(type: number) {
  return typeMap1[type] || type
}

onMounted(async () => {
  // 检查登录状态
  if (!checkLoginStatus()) {
    return
  }

  // 确保VIP数据已加载
  await vipStore.loadVipData()

  resetPagination()
  fetchData()
})

// 监听登录状态变化
watch(() => userStore.isLoggedIn, (isLoggedIn) => {
  if (isLoggedIn && showAuthDialog.value) {
    // 用户登录成功，关闭弹窗并加载数据
    showAuthDialog.value = false
    resetPagination()
    fetchData()
  }
})
</script>

<style lang="scss" scoped>
.bonus-table-container {
  border-radius: 8px;
  padding: 6px 6px 0 6px;
}

.bonus-table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
}

.date-select {
  position: relative;
  width: 160px;
}

.date-btn {
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.5);
  // background-size: 100% 100%;
  // border: none;
  color: #fff;
  padding: 0 10px 0 8.5px;
  // font-size: 13px;
  // font-weight: 500;
  // cursor: pointer;
  // position: relative;
  display: flex;
  align-items: center;
}

.arrow {
  display: inline-block;
  margin-left: 6px;
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid #FFF;
  transition: transform 0.2s;
}

.arrow.up {
  transform: rotate(180deg);
}

.arrow.down {
  transform: rotate(0deg);
}

.date-options {
  position: absolute;
  top: 38px;
  left: 0;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  display: flex;
  z-index: 10;
  padding: 10px 8px;
  gap: 8px;
  width: max-content;
  min-width: unset;
  max-width: 100vw;
}

.option-btn {
  background: transparent;
  border: 1px solid #40b665;
  color: #40b665;
  border-radius: 8px;
  padding: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  min-width: 60px;
  text-align: center;
  white-space: nowrap;
}

.option-btn.active,
.option-btn:hover {
  background: linear-gradient(90deg, #40b665 0%, #1ecb7b 100%);
  color: #fff;
  border: 1px solid #40b665;
}



.bonus-table {}

.table-header {
  display: flex;
  color: #fff;
  font-size: 10px;
  font-weight: 500;
  gap: 0;
}

.th {
  flex: 1;
  text-align: center;
  padding: 8px 0;
}

.table-row {
  display: flex;
  font-size: 12px;
  color: #fff;
  align-items: center;
  min-height: 45px;
}

.table-row .td {
  flex: 1;
  text-align: center;
  padding: 8px 0;
}

.table-row.zebra {
  background: #00563B;
}

.empty-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 115px 0;
  height: 60vh;
}

.empty-text {
  color: #ADB1B0;
  font-size: 11px;
  margin-top: 21px;
}
</style>
